import type { Params } from 'react-router-dom'
import { useParams } from 'react-router-dom'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { Features } from 'generated/featureDefinitions'
import { CustomPeriodsPageDocument, type CustomPeriodsPageQuery } from 'generated/resources/graphql'
import moment from 'moment'

import Loading from 'modules/core/components/Loading'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useQuery } from 'modules/core/hooks/useQuery'
import { dateToString } from 'modules/core/lib/dates'
import FiltersForm from 'modules/core/lib/forms/Filters'
import { range } from 'modules/core/lib/frequencies'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import TimeTracking from 'modules/attendance/components/TimeTracking'
import CustomPeriodForm, {
  CUSTOM_PERIOD_TYPES,
} from 'modules/attendance/components/TimeTracking/Layout/form'
import TimeTrackingCollection from 'modules/attendance/components/TimeTrackingCollection'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { TimeTrackingViewType } from 'modules/attendance/lib/useCustomPeriodPath'
import attendanceCustomPeriods, {
  CustomPeriodsProvider,
} from 'modules/attendance/store/collections/customPeriods'
import employeeStats, {
  EmployeeStatsProvider,
} from 'modules/attendance/store/collections/employeeStats'

import useFetchCustomPeriods from './useFetchCustomPeriods'

export type ClockInStatus = NonNullable<
  CustomPeriodsPageQuery['timeInsights']
>['clockInStatusesConnection']['nodes'][number]

function periodFromParams(params: Readonly<Params<string>>): moment.Moment {
  const { year, month, day } = params
  const date = moment([Number(year), Number(month) - 1, Number(day)])

  return date.isValid() ? date : moment()
}

const CustomPeriodsPage = () => {
  // TODO: move calculation of [from, to] into custom period forms and the params too,
  // Reason: so we can have a single source of truth for this information
  // and not fetching the dates both from custom field and params
  const params = useParams()
  const dateFromParams = periodFromParams(params)
  const { periodType = '' } = params
  const isoWeekly = true
  const today = moment()
  const [from = today, to = today] = range(dateFromParams, periodType, isoWeekly)
  const [viewType, setViewType] = useLocalStorage<TimeTrackingViewType>(
    LOCAL_STORAGE_KEYS.timeTrackingViewType
  )

  const { loading: loadingAttendanceFeatures, featureAvailability: hasTimeTrackingCollection } =
    useAttendanceFeatures('dev_time_tracking_collection')

  // While attendance features are loading, don't start legacy data fetching.
  // This prevents momentary fallbacks to legacy Time tracking.
  const skipLegacyTimeTracking = loadingAttendanceFeatures || hasTimeTrackingCollection

  const customPeriodForm = useLazyRef(
    () =>
      new CustomPeriodForm({
        period_type: periodType,
        start_on: dateToString(from, 'backend'),
        end_on: dateToString(to, 'backend'),
      })
  ).current

  useEffect(() => {
    const fromMonth = from.month() + 1
    const hasViewTypeChanged = viewType?.periodType !== periodType || viewType.month !== fromMonth

    if (hasViewTypeChanged) {
      setViewType({
        periodType,
        year: from.year(),
        month: fromMonth,
        day: from.date(),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodType])

  const { loading: loadingCustomPeriods } = useFetchCustomPeriods(
    customPeriodForm,
    periodType,
    skipLegacyTimeTracking
  )

  const { loading: loadingTimeoffOvertimeCompensation, featureAvailability: hasOvertimeTimeOff } =
    useAttendanceFeatures(Features.timeoff_overtime_compensation)

  const { featureAvailability: hasRealTimeAttendance } = useAttendanceFeatures(
    Features.time_tracking_real_time_attendance
  )

  const isDailyTab = hasRealTimeAttendance && periodType === CUSTOM_PERIOD_TYPES.daily
  const showHourDistributionColumn = !isDailyTab
  const { year, month, day } = useParams()
  const now = new Date()
  const isToday =
    Number(year) == now.getFullYear() &&
    Number(month) == now.getMonth() + 1 &&
    Number(day) == now.getDate()
  const canSendEmailReminder = isDailyTab && isToday

  const { loading: loadingGraphQL, data } = useQuery(CustomPeriodsPageDocument, {
    variables: {
      endOn: dateToString(moment(), 'backend'),
      startOn: dateToString(moment(), 'backend'),
      includeClockInStatuses: hasRealTimeAttendance && canSendEmailReminder,
    },
    skip: skipLegacyTimeTracking,
  })
  const canApproveAttendance = !!data?.attendance._policies.companyApproveAttendance.can
  const clockInStatuses = data?.timeInsights?.clockInStatusesConnection.nodes ?? []
  const canManageLocation = data?.locations._policies.locationManage.can ?? false
  const canAccessEditSettings = data?.attendance._policies.accessEditSettings.can ?? false
  const loading = loadingCustomPeriods || loadingTimeoffOvertimeCompensation || loadingGraphQL

  const filtersForm = useLazyRef(
    () =>
      new FiltersForm({
        sortAttribute: 'first_name',
        sortDirection: 'asc',
        filterBy: 'activity___active',
      })
  ).current

  return loadingAttendanceFeatures ? (
    <Loading />
  ) : (
    <>
      {hasTimeTrackingCollection ? (
        <TimeTrackingCollection />
      ) : (
        <EmployeeStatsProvider collection={employeeStats}>
          <CustomPeriodsProvider collection={attendanceCustomPeriods}>
            <TimeTracking
              loading={loading}
              customPeriodForm={customPeriodForm}
              filtersForm={filtersForm}
              hasOvertimeTimeOff={hasOvertimeTimeOff}
              canApproveAttendance={canApproveAttendance}
              clockInStatuses={clockInStatuses}
              canSendEmailReminder={canSendEmailReminder}
              showHourDistributionColumn={showHourDistributionColumn}
              canManageLocation={canManageLocation}
              canAccessEditSettings={canAccessEditSettings}
            />
          </CustomPeriodsProvider>
        </EmployeeStatsProvider>
      )}
    </>
  )
}

export default CustomPeriodsPage
